/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment settings form utilities
 * @author Satyaki Bose
 * @file assignmentSettingsFormUtils.js
 */

import {
  BASE_VALUE_MAX,
  BASE_VALUE_MIN,
  EMPTY_ASSIGNMENT_SETTINGS_VALUES,
  LATE_WORK_PERCENT_MAX,
  LATE_WORK_PERCENT_MIN
} from './assignmentSettingsConstants';

export function parsePositiveNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed;
}

function normalizeNumericRangeInput(raw, min, max) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits === '') {
    return '';
  }
  const parsed = Number(digits);
  if (parsed > max) {
    return String(max);
  }
  if (parsed < min) {
    return String(min);
  }

  return String(parsed);
}

/**
 * Formats the base value input to ensure it is within the valid range
 */
export function normalizeBaseValueInput(raw) {
  return normalizeNumericRangeInput(raw, BASE_VALUE_MIN, BASE_VALUE_MAX);
}

/**
 * Formats the late work percent input to ensure it is within the valid range
 */
export function normalizeLateWorkPercentInput(raw) {
  return normalizeNumericRangeInput(raw, LATE_WORK_PERCENT_MIN, LATE_WORK_PERCENT_MAX);
}

/**
 * builds the default values for the settings form
 */
export function buildFormDefaultValues(initialValues = {}) {
  const merged = {
    ...EMPTY_ASSIGNMENT_SETTINGS_VALUES,
    ...initialValues
  };

  return {
    ...merged,
    equalWeightingBaseValue: normalizeBaseValueInput(merged.equalWeightingBaseValue),
    lateWorkPercent: normalizeLateWorkPercentInput(merged.lateWorkPercent)
  };
}

/**
 * Determines if the save button should be disabled
 */
export function isSaveDisabled({
  isValid,
  isSubmitting,
  disableAllFields,
  requireDirtyToSave = false,
  isDirty = true
}) {
  if (disableAllFields || isSubmitting) {
    return true;
  }

  if (!isValid) {
    return true;
  }

  if (requireDirtyToSave && !isDirty) {
    return true;
  }

  return false;
}
